import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, TEST_API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";
import { useBranch } from "contexts/BranchContext";
import { useAuthSession } from "hooks/useAuthSession";

// Component imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import Footer from "components/layout/Footer/Footer";
import BottomSheet from "components/ui/BottomSheet/BottomSheet";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// CSS imports
import 'styles/Font.css';

// Image imports
import starIcon from 'assets/images/icon/star-icon.svg';
import dotIcon from 'assets/images/icon/dot-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';


function PtDetail() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const navigate = useNavigate();
    const location = useLocation();

    const { currentBranch, selectBranch } = useBranch();
    const { userInfo } = useAuthSession({ requireAuth: true });
    const brKey = currentBranch?.brKey || '1';

    // Home(Pt.jsx)에서 넘겨준 키 값 (없을 경우 대비)
    // const {mnKey, stKind = 'C'} = location.state || {};
    const { mnKey, stKind = 'C', trainer: passedTrainer, date: passedDate } = location.state || {};

    // 화면 표시용 상태 (넘겨받은 데이터가 있으면 그것으로 초기화)
    const [trainer, setTrainer] = useState(passedTrainer);
    const [isLoading, setIsLoading] = useState(true);

    const [showSheet, setShowSheet] = useState(false);
    const [sheetTimes, setSheetTimes] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);
    const [currentSelectedDate, setCurrentSelectedDate] = useState(passedDate || "");
    const [slickStatus, setSlickStatus] = useState("N");
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // 이용권 보유 여부 체크
    useEffect(() => {
        const fetchUserStatus = async () => {
            const { token } = getTokenStatus();
            if (!token) return;

            try {
                const response = await fetch(`${API_BASE_URL}/home`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({ brKey: brKey })
                });

                if (response.ok) {
                    const data = await response.json();
                    const resData = data.data || data;

                    if (resData.slickStatus) {
                        setSlickStatus(resData.slickStatus);
                    }

                    // 전역 지점 정보가 서버 정보와 다르거나 비어있다면 업데이트
                    if (resData.brKey && (resData.brKey !== currentBranch?.brKey || resData.brName !== currentBranch?.brName)) {
                        selectBranch({
                            brKey: resData.brKey,
                            brName: resData.brName || '',
                            isProductAvailable: resData.brKey === '2' ? 'Y' : (resData.isProductAvailable || 'N'),
                            availableStKinds: resData.stKind ? resData.stKind.split(',').map(s => s.trim()) : ['E'],
                            footerInfo: resData.footerInfo ? {
                                companyName: resData.footerInfo.companyName || '',
                                ceoName: resData.footerInfo.ceoName || '',
                                businessNumber: resData.footerInfo.businessNumber || '',
                                ecommerceNumber: resData.footerInfo.ecommerceNumber || '',
                                address: resData.footerInfo.address || '',
                                csPhone: resData.footerInfo.csPhone || '',
                                email: resData.footerInfo.email || ''
                            } : {
                                companyName: resData.brCompanyName || '',
                                ceoName: resData.brCeoName || '',
                                businessNumber: resData.brBizNo || '',
                                ecommerceNumber: resData.brMailOrderNo || '',
                                address: resData.brAddr || '',
                                csPhone: resData.brTel || '',
                                email: resData.brEmail || ''
                            }
                        });
                    }
                }
            } catch (e) {
                console.error("Failed to fetch user status:", e);
            }
        };

        if (userInfo) {
            fetchUserStatus();
        }
    }, [userInfo, brKey, currentBranch, selectBranch]);

    // 예약 스케줄 날짜 변경 시 조회
    const handleDateChange = useCallback(async (date) => {
        setCurrentSelectedDate(date);
        const { token } = getTokenStatus();
        if (!token || !mnKey) return;

        try {
            // const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&mnKey=${mnKey}&stKind=${stKind}&date=${date}`;
            const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&mnKey=${mnKey}&stKind=${stKind}&date=${date}`;
            const response = await fetch(SCHEDULE_API_URL, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            let times = [];
            if (data.list && Array.isArray(data.list)) {
                times = data.list;
            } else if (data.mnInfo && data.mnInfo.times) {
                times = data.mnInfo.times;
            } else if (data.times) {
                times = data.times;
            }

            setSheetTimes(times);

        } catch (e) {
            console.error("Schedule Fetch Error:", e);
            setSheetTimes([]);
        }
    }, [mnKey, stKind, brKey]);

    // 예약하기 클릭 핸들러 (이용권 보유 여부 사전 검증)
    const handleReservationClick = useCallback(async () => {
        if (slickStatus !== 'Y') {
            setAlertModal({
                isOpen: true,
                title: '예약 불가',
                message: `[${currentBranch?.brName}] 수업 예약 권한이 없습니다.\n(이용권을 구매해주세요)`
            });
            return;
        }

        let targetDate = currentSelectedDate;
        if (!targetDate) {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            targetDate = `${year}-${month}-${day}`;
        }

        await handleDateChange(targetDate);
        setShowSheet(true);
    }, [handleDateChange, currentSelectedDate, slickStatus]);

    const handleCloseSheet = () => setShowSheet(false);

    const handleSheetConfirm = useCallback((data) => {
        setPendingReservation(data);
        setIsConfirmModalOpen(true);
    }, []);

    // 최종 예약 등록 처리
    const handleFinalReservation = useCallback(async () => {
        if (!pendingReservation) return;

        const { stKey } = pendingReservation;
        const { token } = getTokenStatus();

        const payload = {
            brKey: brKey,
            stKey: String(stKey),
            stKind: stKind
        };

        try {
            const response = await fetch(`${API_BASE_URL}/regSchedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const resData = await response.json();
            const code = resData.data?.code || resData.code;
            const isSuccess = code === 200 || code === '200' || code === '0000';

            if (isSuccess) {
                setIsSuccessModalOpen(true);
            } else {
                const errorMsg = resData.data?.message || resData.message || '오류가 발생했습니다.';
                // alert(`예약 실패: ${errorMsg}`);
                alert(errorMsg);
            }

        } catch (e) {
            console.error("Reservation Error:", e);
            alert("예약 요청 중 오류가 발생했습니다.");
        }

        setIsConfirmModalOpen(false);
        setPendingReservation(null);
    }, [pendingReservation, stKind, brKey]);

    const handleCloseConfirmModal = () => {
        setIsConfirmModalOpen(false);
        setPendingReservation(null);
    };

    const handleCloseSuccessModal = () => {
        setIsSuccessModalOpen(false);
    };

    const closeAlertModal = () => {
        setAlertModal(prev => ({ ...prev, isOpen: false }));
    };

    // [API 호출] 트레이너 상세 정보 조회
    useEffect(() => {
        const { token } = getTokenStatus(); // 토큰 필요 시 주석 해제
        const fetchTrainerDetail = async () => {
            if (!mnKey) {
                setIsLoading(false);
                return;

            }
            try {
                const queryParams = new URLSearchParams({
                    mnKey: mnKey,
                    stKind: stKind // PT는 보통 'C' (개인PT)
                }).toString();

                const response = await fetch(`${API_BASE_URL}/managerView?${queryParams}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const data = await response.json();
                const resData = data.data || data;

                // [데이터 매핑] 서버 응답 -> UI 상태
                if (resData.mnInfo) {
                    const info = resData.mnInfo;
                    setTrainer({
                        name: info.coachName || info.name || '트레이너 이름',
                        intro: info.cont || info.introduction || '',
                        img: info.img || defaultCoachImg,
                        rating: (info.rating && !isNaN(Number(info.rating))) ? Number(info.rating).toFixed(1) : '0.0',
                        reviews: info.reviews || 0,
                        detail: info.cont || info.description || '', // 상세 내용
                        qualification: info.qualification || '',     // 자격
                        career: info.career || ''                    // 이력
                    });
                }
            } catch (e) {
                console.error("Trainer Detail Fetch Error:", e);
                // 에러 시 defaultTrainer가 유지됨
                if (passedTrainer) setTrainer(passedTrainer);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTrainerDetail();
    }, [mnKey, stKind, passedTrainer]);

    // ▼▼▼ [수정] 문의하기 버튼 클릭 핸들러 ▼▼▼
    const handleInquiryClick = useCallback(() => {
        // mnKey가 없으면 경고 (또는 기본값 처리)
        if (!mnKey) {
            console.error("트레이너 키(mnKey)가 없습니다.");
            // return; // 필요 시 중단
        }

        // 문의 페이지로 이동하며 트레이너 정보 전달
        navigate('/pt/inquiry', {
            state: {
                mnKey: mnKey,             // 트레이너 고유 키 (API 전송용)
                trainerName: trainer.name // 화면 표시용 이름
            }
        });
    }, [navigate, mnKey, trainer]);
    // ▲▲▲ ---------------------------------- ▲▲▲

    // 이미지 리스트 처리 (API 배열 데이터 대응)
    const trainerImages = useMemo(() => {
        const imgData = trainer.img;

        // API에서 배열로 이미지를 준 경우 (다중 이미지)
        if (Array.isArray(imgData) && imgData.length > 0) {
            return imgData;
        }

        // 단일 문자열로 이미지를 준 경우
        if (typeof imgData === 'string' && imgData.trim() !== '') {
            return [imgData];
        }

        // 이미지가 없는 경우 기본 이미지 표시
        return [defaultCoachImg];
    }, [trainer.img]);

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    /* --- 동적 높이 측정 --- */
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        measureHeight();

        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };

        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);

    const dynamicMainContentStyle = useMemo(() => ({
        ...staticStyles.mainContent,
        // paddingTop: `${headerHeight}px`,
        paddingTop: '0px',
        paddingBottom: '100px',
        paddingLeft: '0',
        paddingRight: '0',
        // }), [headerHeight]);
    }), []);

    if (isLoading && !trainer.name) {
        return <div style={{
            ...staticStyles.backgroundWrapper,
            color: 'white',
            justifyContent: 'center',
            alignItems: 'center'
        }}>로딩 중...</div>;
    }

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                {/*<div className="statusBar" style={staticStyles.statusBar}/>*/}
                <TopNavigator title="" isTransparent={true} />
            </div>

            <main style={dynamicMainContentStyle}>
                {/* 1. 트레이너 프로필 이미지 (가로 스크롤) */}
                <div style={staticStyles.imageContainer}>
                    <style>{`
                        .hide-scrollbar::-webkit-scrollbar { display: none; }
                        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    `}</style>

                    <div className="hide-scrollbar" style={staticStyles.imageScrollContainer}>
                        {trainerImages.map((imgSrc, idx) => (
                            <img
                                key={idx}
                                src={imgSrc}
                                alt={`트레이너 프로필 ${idx + 1}`}
                                style={staticStyles.coachImage}
                            />
                        ))}
                    </div>
                    <div style={staticStyles.imageOverlay} />
                    <div style={staticStyles.pageIndicator}>
                        <span style={staticStyles.pageIndicatorText}>
                            옆으로 넘겨 더보기 👉
                        </span>
                    </div>
                </div>

                {/* 2. 기본 정보 */}
                <div style={staticStyles.infoContainer}>
                    <div style={staticStyles.nameRow}>
                        <span style={staticStyles.coachName}>{trainer.name}</span>
                    </div>

                    <div style={staticStyles.reviewRow}>
                        <img src={starIcon} alt="별점" style={staticStyles.starIcon} />
                        <span style={staticStyles.ratingText}>
                            {trainer.rating}
                        </span>
                        <img src={dotIcon} alt="dot" style={staticStyles.dotIcon} />
                        <span style={staticStyles.reviewText}>
                            리뷰 {trainer.reviews}개
                        </span>
                    </div>

                    {trainer.intro && (
                        /*<p style={staticStyles.shortIntro}>{trainer.intro}</p>*/
                        <p style={staticStyles.detailText}>
                            {trainer.detail || trainer.description}
                        </p>
                    )}
                </div>

                <div style={staticStyles.divider} />

                {/* 3. 상세 정보 (자격/이력/소개) */}
                <div style={staticStyles.detailSection}>
                    {(trainer.qualification || trainer.career) && (
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={staticStyles.sectionTitle}>자격 및 이력</h3>
                            <div style={staticStyles.careerList}>
                                {trainer.qualification && (
                                    <p style={staticStyles.detailText}>{trainer.qualification}</p>
                                )}
                                {trainer.career && (
                                    <p style={{ ...staticStyles.detailText, marginTop: '8px' }}>{trainer.career}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/*<h3 style={staticStyles.sectionTitle}>트레이너 소개</h3>
                    <p style={staticStyles.detailText}>
                        {trainer.detail || trainer.description}
                    </p>*/}
                </div>

                {/* Footer */}
                <div style={{ padding: '0 24px' }}>
                    <Footer />
                </div>
            </main>

            {/* 4. 하단 버튼 */}
            <div style={staticStyles.bottomButtonWrapper}>
                <div style={staticStyles.buttonRow}>
                    <button style={staticStyles.inquiryButton} onClick={handleInquiryClick}>
                        문의하기
                    </button>
                    <button style={staticStyles.reservationButton} onClick={handleReservationClick}>
                        예약하기
                    </button>
                </div>
            </div>

            <BottomSheet
                show={showSheet}
                onClose={handleCloseSheet}
                onConfirm={handleSheetConfirm}
                initialData={{ coachName: trainer?.name }}
                availableTimes={sheetTimes}
                selectedDate={currentSelectedDate}
                onDateChange={handleDateChange}
                brName={currentBranch?.brName}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleFinalReservation}
                title="예약 확인"
                message={pendingReservation ? `${pendingReservation.coachName}\n${pendingReservation.date} ${pendingReservation.time}\n\n선택하신 일시로 예약하시겠습니까?` : ""}
            />

            <ConfirmModal
                isOpen={isSuccessModalOpen}
                onClose={handleCloseSuccessModal}
                onConfirm={handleCloseSuccessModal}
                title="예약 완료"
                message="예약이 확정되었습니다."
            />

            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={closeAlertModal}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

/** @type {React.CSSProperties} */
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: 'transparent', zIndex: 100,
    },
    mainContent: {
        flex: 1, width: '100%', minHeight: 0, overflowY: 'auto', display: 'flex',
        flexDirection: 'column', scrollbarWidth: 'none', msOverflowStyle: 'none',
    },

    // 이미지 스타일 (BoostDetail과 동일)
    imageContainer: {
        width: '100%', aspectRatio: '1/1', position: 'relative',
        flexShrink: 0, marginTop: '0', overflow: 'hidden',
    },
    imageScrollContainer: {
        display: 'flex', width: '100%', height: '100%', overflowX: 'auto',
        scrollSnapType: 'x mandatory', scrollBehavior: 'smooth',
    },
    coachImage: {
        minWidth: '100%', height: '100%', objectFit: 'cover', scrollSnapAlign: 'start',
    },
    imageOverlay: {
        position: 'absolute', bottom: 0, left: 0, width: '100%', height: '50%',
        background: 'linear-gradient(to top, #0D042D 0%, transparent 100%)',
        pointerEvents: 'none',
    },
    pageIndicator: {
        position: 'absolute', bottom: '20px', right: '20px', padding: '4px 8px', borderRadius: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 2,
    },
    pageIndicatorText: {
        color: '#FFFFFF', fontSize: '12px', fontFamily: 'Pretendard',
    },

    // 정보 영역
    infoContainer: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px' },
    nameRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    coachName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '24px', color: '#FCFCFA' },
    shortIntro: {
        backgroundColor: '#212836',
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '16px', color: '#CCCCCC', lineHeight: '1.5',
    },
    reviewRow: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' },
    reviewText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#9A9A9A' },
    ratingText: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '14px', color: '#FCFCFA' },

    starIcon: { width: '16px', height: '16px' },
    dotIcon: { width: '4px', height: '4px' },

    // divider: {width: '100%', height: '8px', backgroundColor: '#FFFFFF'}, //backgroundColor: '#161622'

    // 상세 소개 및 경력 섹션
    detailSection: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
    sectionTitle: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#FCFCFA', margin: 0 },
    detailText: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '15px', color: '#B0B0B0',
        lineHeight: '1.6', whiteSpace: 'pre-line',
    },

    // 자격 및 경력 리스트 스타일
    careerList: {
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: '8px',
    },
    careerItem: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '15px', color: '#B0B0B0',
        display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.5',
    },
    bulletPoint: {
        color: '#8052FF', fontWeight: 'bold',
    },

    // 하단 버튼 Wrapper (위치, 배경, 패딩 담당)
    bottomButtonWrapper: {
        position: 'fixed', bottom: 0, left: 0, width: '100%',

        // 여기에만 패딩을 줍니다. (상, 우, 하, 좌)
        padding: '24px 24px calc(env(safe-area-inset-bottom, 24px) + 24px) 24px',

        backgroundColor: '#0D042D', zIndex: 100, boxSizing: 'border-box',
    },

    // 버튼 가로 배치 컨테이너 (패딩 제거)
    buttonRow: {
        display: 'flex', gap: '10px', width: '100%',
    },

    // [유지] 문의하기 버튼 (회색)
    inquiryButton: {
        flex: 1, width: '100%', height: '56px', borderRadius: '12px', border: '1px solid #3A394A',
        background: '#212836', color: '#FFFFFF', fontFamily: 'Pretendard', fontWeight: 700,
        fontSize: '16px', cursor: 'pointer',
    },

    // [유지] 예약하기 버튼 (그라데이션)
    reservationButton: {
        flex: 2, height: '56px', borderRadius: '12px', border: 'none', color: '#FFFFFF', fontFamily: 'Pretendard',
        fontWeight: 700, fontSize: '16px', cursor: 'pointer',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
    },
};

export default PtDetail;